import { useStore } from '@nanostores/react';
import { AnimatePresence, motion } from 'framer-motion';
import { memo } from 'react';
import { IconButton } from '~/components/ui/IconButton';
import type { Snapshot } from '~/lib/snapshots/snapshot';
import { versionHistoryStore } from '~/lib/stores/version-history/version-history';
import { classNames } from '~/utils/classNames';

interface VersionSnapshotCardProps {
  snapshot: Snapshot;
  versionNumber: number;
  isCurrentVersion: boolean;
  handleRollback: (e: React.MouseEvent) => void;
  handlePreview: (e: React.MouseEvent) => void;
}

export const VersionSnapshotCard = memo<VersionSnapshotCardProps>(
  ({ snapshot, versionNumber, isCurrentVersion, handleRollback, handlePreview }) => {
    const isSaving = useStore(snapshot.isSaving);

    const handleRollbackClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleRollback(e);
    };

    const handlePreviewClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handlePreview(e);
    };

    return (
      <div>
        <AnimatePresence mode="popLayout">
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.75, filter: 'blur(12px)' }}
            animate={{ y: 0, opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ y: -20, opacity: 0, scale: 0.75, filter: 'blur(12px)' }}
            transition={{ type: 'easeOut', duration: 0.25 }}
            className="flex items-center gap-2 whitespace-nowrap"
            style={{ originX: 0 }}
          >
            <div className="inline-block min-w-[240px]">
              <div
                className={classNames(
                  'relative h-16 flex items-center justify-between p-3 py-2 rounded-lg border transition-colors mt-2 mb-6 min-w-[240px] w-fit max-w-[360px]',
                  {
                    'bg-bolt-ds-brand/5 dark:bg-bolt-ds-brand/7 border-bolt-ds-brand/20': isCurrentVersion,
                    'bg-bolt-elements-background-depth-1 border-bolt-elements-borderColor hover:bg-bolt-elements-background-depth-2':
                      !isCurrentVersion,
                  },
                )}
              >
                <div className={classNames('flex-col min-w-0', isCurrentVersion ? 'pr-0' : 'pr-20')}>
                  <div className="flex items-center gap-1">
                    {isSaving ? (
                      <div className="h-3 w-40 rounded bg-bolt-elements-background-depth-3 animate-pulse" />
                    ) : (
                      <div
                        className="font-medium text-sm text-bolt-ds-textPrimary truncate"
                        style={{ maxWidth: 300 }}
                        title={snapshot.displayTitle}
                      >
                        {snapshot.displayTitle}
                      </div>
                    )}
                    <IconButton
                      icon={snapshot.isBookmarked ? 'i-heroicons:bookmark-solid' : 'i-heroicons:bookmark'}
                      size="xs"
                      title="Bookmark"
                      variant="ghost"
                      disabled={isSaving}
                      color="brand"
                      onClick={(e) => {
                        e.stopPropagation();
                        versionHistoryStore.toggleBookmark(snapshot);
                      }}
                    />
                  </div>
                  <div className="text-xs text-bolt-ds-textSecondary opacity-70 pt-1">
                    {isSaving ? (
                      <span className="inline-block h-3 w-24 rounded bg-bolt-elements-background-depth-3 animate-pulse" />
                    ) : (
                      <>Version {versionNumber}</>
                    )}
                  </div>
                </div>

                {!isCurrentVersion && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                    <IconButton
                      icon="i-heroicons:eye"
                      size="sm"
                      title="Preview this version"
                      variant="ghost"
                      disabled={isSaving}
                      onClick={handlePreviewClick}
                    />
                    <IconButton
                      icon="i-heroicons:arrow-uturn-left"
                      size="sm"
                      title="Restore this version"
                      variant="ghost"
                      disabled={isSaving}
                      onClick={handleRollbackClick}
                    />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  },
);

VersionSnapshotCard.displayName = 'VersionSnapshotCard';
